import { DONE_STATUS } from '../data/types'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, ChevronLeft, ChevronRight, Circle, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '../components/Button'
import { Drawer } from '../components/Drawer'
import { EmptyState } from '../components/EmptyState'
import { FilterBar, FilterSelect, SegmentedControl } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { PriorityBadge } from '../components/PriorityBadge'
import { useAppData, type DirectoryEntry } from '../context/AppDataContext'
import { useWorkspace } from '../context/WorkspaceContext'
import type { Client } from '../data/types'
import { useToast } from '../context/ToastContext'
import { useScopedWork } from '../hooks/useScopedWork'
import { cn } from '../lib/cn'
import { readableAccent, useResolvedTheme } from '../lib/color'
import { formatDateLong, isoDate, today } from '../lib/format'
import { monthMatrix, weekAround, weekdayNames } from '../lib/monthGrid'
import { NewTaskModal } from './board/NewTaskModal'

export default function CalendarPage() {
  const resolvedTheme = useResolvedTheme()
  const { setTaskStatus, toggleDeliverable, directory, month, setMonth } = useAppData()
  const { weekStartsOn } = useWorkspace()
  const { tasks, deliverables, content, clients, isScoped } = useScopedWork()
  const activeClients = clients.filter(c => c.accountStatus !== 'Former' && c.accountStatus !== 'Lost')
  const { showToast } = useToast()
  const [view, setView] = useState<'month' | 'week' | 'day'>('month')
  // Opens on the month the rest of the app is on, and moving it moves them:
  // the month-end commitments and the content plan are read a month at a
  // time, so a calendar on June over data for September showed nothing.
  const [cursor, setCursor] = useState(() => (month === `${today().slice(0, 7)}-01` ? new Date() : new Date(`${month}T00:00`)))
  useEffect(() => {
    const m = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-01`
    if (m !== month) setMonth(m)
    // Only the cursor drives this; the month following it is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor])
  const [direction, setDirection] = useState<1 | -1>(1)
  const [selectedDate, setSelectedDate] = useState(today)
  const [clientFilter, setClientFilter] = useState('all')
  const [assigneeFilter, setAssigneeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  const [daySheetOpen, setDaySheetOpen] = useState(false)

  /** Picking a date opens the agenda as a sheet on phones; on desktop the
   * side panel is always visible, so it just updates that. */
  function selectDate(key: string) {
    setSelectedDate(key)
    if (!window.matchMedia('(min-width: 1024px)').matches) setDaySheetOpen(true)
  }

  // Close the sheet if the viewport grows past the breakpoint mid-session,
  // otherwise it would sit over a layout that already shows the agenda.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => mq.matches && setDaySheetOpen(false)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const events = useMemo(() => {
    const taskEvents = tasks
      .filter((t) => (clientFilter === 'all' || t.clientId === clientFilter) && (assigneeFilter === 'all' || t.assigneeId === assigneeFilter))
      .filter((t) => statusFilter === 'all' || t.status === statusFilter)
      .map((t) => ({ id: t.id, date: t.dueDate, title: t.title, kind: 'task' as const, clientId: t.clientId, priority: t.priority, status: t.status, assigneeId: t.assigneeId }))
    const deliverableEvents = deliverables
      .filter((d) => clientFilter === 'all' || d.clientId === clientFilter)
      .filter((d) => assigneeFilter === 'all' || d.assignedToId === assigneeFilter)
      .filter((d) => statusFilter === 'all' || d.status === statusFilter)
      .map((d) => ({ id: d.id, date: d.dueDate, title: d.name, kind: 'deliverable' as const, clientId: d.clientId, priority: 'Medium' as const, status: d.status, assigneeId: d.assignedToId }))
    // The content plan's pieces that are not open on the board — posted ones,
    // mostly — on the day they go out, so the month shows what went out as
    // well as what is due.
    const open = new Set(tasks.map((t) => t.id))
    const contentEvents = content
      .filter((c) => c.date !== '' && !open.has(c.id))
      .filter((c) => (clientFilter === 'all' || c.clientId === clientFilter) && (assigneeFilter === 'all' || c.assignedToId === assigneeFilter))
      .filter((c) => statusFilter === 'all' || c.status === statusFilter)
      .map((c) => ({ id: c.id, date: c.date, title: c.title, kind: 'task' as const, clientId: c.clientId, priority: 'Medium' as const, status: c.status, assigneeId: c.assignedToId }))
    return [...taskEvents, ...contentEvents, ...deliverableEvents]
  }, [tasks, content, deliverables, clientFilter, assigneeFilter, statusFilter])

  const eventsByDay = useMemo(() => {
    const map = new Map<string, typeof events>()
    events.forEach((e) => {
      if (!map.has(e.date)) map.set(e.date, [])
      map.get(e.date)!.push(e)
    })
    return map
  }, [events])

  const monthCells = useMemo(
    () => monthMatrix(cursor.getFullYear(), cursor.getMonth(), weekStartsOn),
    [cursor, weekStartsOn],
  )
  const todayKey = today()
  const weekdays = weekdayNames(weekStartsOn)
  const selectedEvents = eventsByDay.get(selectedDate) ?? []

  function shift(delta: number) {
    setDirection(delta > 0 ? 1 : -1)
    if (view === 'month') setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1))
    else if (view === 'week') setCursor(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + delta * 7))
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + delta))
  }

  // No wheel navigation: scrolling the page with the pointer over the grid
  // flipped the month, which is the last thing somebody scrolling expects.
  // The arrow buttons and the Today button move it.

  function completeEvent(e: (typeof events)[number]) {
    // Only a piece. A commitment is satisfied by pieces reaching posted and
    // counted by the server, so there is nothing to tick — and the version of
    // this that called `toggleDeliverable` and then toasted "Updated
    // successfully." reported a success over a call that does nothing.
    if (e.kind !== 'task') return
    const posted = e.status === DONE_STATUS
    const was = e.status
    setTaskStatus(e.id, posted ? 'Editing' : DONE_STATUS)
    showToast(posted ? 'Back to editing.' : 'Marked posted.', 'success', {
      undo: () => setTaskStatus(e.id, was),
    })
  }

  // A rolling 7-day window centered on the cursor rather than a
  // Sunday-locked calendar week — this way *any* shift amount (the arrow
  // buttons' full week, or the wheel's 4-day step below) always visibly
  // slides the strip, instead of sometimes landing back inside the same
  // Sun–Sat week and looking like the scroll did nothing.
  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 3 + i))
  }, [cursor])

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Calendar"
        description="When everything is going out, and what is owed by month end."
        actions={
          <Button onClick={() => setNewTaskOpen(true)}>
            <Plus size={16} /> Add a piece
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterBar>
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as typeof view)}
            options={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'day' },
            ]}
          />
          <FilterSelect
            label="Filter by client"
            value={clientFilter}
            onChange={setClientFilter}
            options={[{ label: 'All clients', value: 'all' }, ...activeClients.map((c) => ({ label: c.name, value: c.id }))]}
          />
          {!isScoped && (
            <FilterSelect
              label="Filter by team member"
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              options={[{ label: 'Anyone', value: 'all' }, ...directory.map((m) => ({ label: m.name, value: m.id }))]}
            />
          )}
          <FilterSelect
            label="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'All statuses', value: 'all' },
              { label: 'Briefed', value: 'Briefed' },
              { label: 'Editing', value: 'Editing' },
              { label: 'Shot', value: 'Shot' },
              { label: 'Review', value: 'Review' },
              { label: DONE_STATUS, value: DONE_STATUS },
            ]}
          />
        </FilterBar>
        <div className="flex items-center gap-2">
          <button
            onClick={() => shift(-1)}
            aria-label="Previous"
            className="rounded-lg border border-canvas-fg/15 p-1.5 text-canvas-fg/70 transition-colors hover:bg-canvas-fg/10 hover:text-canvas-fg"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="relative min-w-[150px] overflow-hidden text-center">
            <AnimatePresence mode="popLayout" initial={false} custom={direction}>
              <motion.p
                key={view === 'day' ? isoDate(cursor) : `${cursor.getFullYear()}-${cursor.getMonth()}-${view === 'week' ? weekDates[0]?.getDate() : ''}`}
                custom={direction}
                initial={{ opacity: 0, x: direction * 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -20 }}
                transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.7 }}
                className="font-display text-base font-bold text-canvas-fg"
              >
                {view === 'day'
                  ? cursor.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </motion.p>
            </AnimatePresence>
          </div>
          <button
            onClick={() => shift(1)}
            aria-label="Next"
            className="rounded-lg border border-canvas-fg/15 p-1.5 text-canvas-fg/70 transition-colors hover:bg-canvas-fg/10 hover:text-canvas-fg"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <div className="rounded-card border border-shell-border bg-shell p-3 shadow-card sm:p-4">
          {view === 'month' && (
            <div>
              <div className="grid grid-cols-7 gap-1 pb-2 text-center text-xs font-semibold text-shell-text">
                {weekdays.map((d) => (
                  <div key={d} className="hidden sm:block">
                    {d}
                  </div>
                ))}
                {weekdays.map((d) => (
                  <div key={d + 'm'} className="sm:hidden">
                    {d[0]}
                  </div>
                ))}
              </div>
              <div className="relative overflow-hidden">
                <AnimatePresence mode="popLayout" initial={false} custom={direction}>
                  <motion.div
                    key={`${cursor.getFullYear()}-${cursor.getMonth()}`}
                    custom={direction}
                    initial={{ opacity: 0, x: direction * 44 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: direction * -44 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 32, mass: 0.9 }}
                    className="grid grid-cols-7 gap-1"
                  >
                    {monthCells.map(({ date, inMonth }) => {
                      const key = isoDate(date)
                      const dayEvents = eventsByDay.get(key) ?? []
                      const isToday = key === todayKey
                      const isSelected = key === selectedDate
                      return (
                        <button
                          key={key}
                          onClick={() => selectDate(key)}
                          className={cn(
                            'flex min-h-[72px] flex-col items-start gap-1 rounded-xl border p-1.5 text-left transition-colors sm:min-h-[92px] sm:p-2',
                            isSelected ? 'border-brand-400 bg-brand-500/15' : 'border-transparent hover:bg-shell-border/40',
                            !inMonth && 'opacity-40',
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium',
                              isToday ? 'bg-brand-500 text-white' : 'text-shell-text',
                            )}
                          >
                            {date.getDate()}
                          </span>
                          <div className="flex w-full flex-col gap-0.5">
                            {dayEvents.slice(0, 2).map((e) => {
                              const client = clients.find((c) => c.id === e.clientId)
                              return (
                                <span
                                  key={e.id}
                                  className="truncate rounded-md px-1.5 py-0.5 text-[11px] font-semibold sm:text-xs"
                                  style={{
                                    backgroundColor: `${client?.color}22`,
                                    color: readableAccent(client?.color, resolvedTheme),
                                  }}
                                >
                                  {e.title}
                                </span>
                              )
                            })}
                            {dayEvents.length > 2 && (
                              <span className="text-[10px] font-medium text-shell-text">+{dayEvents.length - 2} more</span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          )}

          {view === 'week' && (
            <div className="relative overflow-hidden">
              <AnimatePresence mode="popLayout" initial={false} custom={direction}>
                <motion.div
                  key={weekDates[0] ? isoDate(weekDates[0]) : 'week'}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 44 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -44 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 32, mass: 0.9 }}
                  className="grid grid-cols-1 gap-2 sm:grid-cols-7"
                >
              {weekDates.map((date) => {
                const key = isoDate(date)
                const dayEvents = eventsByDay.get(key) ?? []
                const isToday = key === todayKey
                return (
                  <button
                    key={key}
                    onClick={() => selectDate(key)}
                    className={cn(
                      'flex h-64 flex-col gap-1.5 rounded-xl border p-2.5 text-left transition-colors',
                      key === selectedDate ? 'border-brand-400 bg-brand-500/15' : 'border-shell-border hover:bg-shell-border/40',
                    )}
                  >
                    <div className="flex shrink-0 items-center justify-between gap-1">
                      <span className={cn('text-xs font-semibold', isToday ? 'text-brand-500' : 'text-shell-text')}>
                        {date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-[10px] font-medium text-shell-text">
                        {date.toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    {/* Scrolls its own content on wheel/trackpad — unlike
                     * the month view, hovering a week-view day card should
                     * scroll through that day's events, not flip weeks. */}
                    <div className="flex flex-1 flex-col gap-1 overflow-y-auto scrollbar-thin pr-0.5">
                      {dayEvents.map((e) => (
                        <span
                          key={e.id}
                          className="shrink-0 truncate rounded-md bg-shell-border/50 px-1.5 py-1 text-xs font-semibold text-shell-text-active"
                        >
                          {e.title}
                        </span>
                      ))}
                      {dayEvents.length === 0 && <span className="text-[11px] text-shell-text/60">No events</span>}
                    </div>
                  </button>
                )
              })}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {view === 'day' && (
            <div className="flex flex-col gap-2 p-2">
              {(eventsByDay.get(isoDate(cursor)) ?? []).map((e) => {
                const assignee = directory.find((m) => m.id === e.assigneeId)
                const client = clients.find((c) => c.id === e.clientId)
                return (
                  <div key={e.id} className="flex items-center gap-3 rounded-xl border border-shell-border p-3">
                    {/*
                      * A tick on a commitment would do nothing, so it is not
                      * offered. The dot still shows where the row stands.
                      */}
                    {e.kind === 'task' ? (
                      <button
                        onClick={() => completeEvent(e)}
                        aria-label={e.status === DONE_STATUS ? `Move ${e.title} back to editing` : `Mark ${e.title} posted`}
                        className="text-shell-text/60 hover:text-success-500"
                      >
                        {e.status === DONE_STATUS ? <CheckCircle2 size={18} className="text-success-500" /> : <Circle size={18} />}
                      </button>
                    ) : (
                      <span className="text-shell-text/30" aria-hidden="true">
                        <Circle size={18} />
                      </span>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-shell-text-active">{e.title}</p>
                      <p className="text-xs text-shell-text">
                        {client?.name} &middot; {assignee?.name}
                      </p>
                    </div>
                    <PriorityBadge priority={e.priority} />
                  </div>
                )
              })}
              {(eventsByDay.get(isoDate(cursor)) ?? []).length === 0 && (
                <EmptyState title="Nothing on" description="Nothing is going out or due on this day." />
              )}
            </div>
          )}
        </div>

        {/* On phones this same agenda is shown in a bottom sheet on tap
          * instead — stacked underneath the grid it sat below the fold, so
          * tapping a date appeared to do nothing. */}
        <div className="hidden rounded-card border border-shell-border bg-shell p-4 shadow-card lg:block">
          <h3 className="font-display text-sm font-semibold text-shell-text-active">{formatDateLong(selectedDate)}</h3>
          <p className="mt-0.5 text-xs text-shell-text">{selectedEvents.length} item{selectedEvents.length !== 1 && 's'} scheduled</p>
          <div className="mt-4">
            <DayAgenda events={selectedEvents} clients={clients} directory={directory} onToggle={completeEvent} />
          </div>
        </div>
      </div>

      <Drawer open={daySheetOpen} onClose={() => setDaySheetOpen(false)} side="bottom" title={formatDateLong(selectedDate)}>
        <div className="px-5 pb-6 pt-1">
          <p className="mb-3 text-xs text-shell-text">
            {selectedEvents.length} item{selectedEvents.length !== 1 && 's'} scheduled
          </p>
          <DayAgenda events={selectedEvents} clients={clients} directory={directory} onToggle={completeEvent} />
        </div>
      </Drawer>

      <NewTaskModal open={newTaskOpen} onClose={() => setNewTaskOpen(false)} />
    </div>
  )
}

/** The selected day's items — rendered in the desktop side panel and, on
 * phones, inside the bottom sheet. Generic over the event shape so it keeps
 * the caller's exact type rather than widening it. */
function DayAgenda<E extends { id: string; title: string; kind: string; status: string; clientId: string; assigneeId: string }>({
  events,
  clients,
  directory,
  onToggle,
}: {
  events: E[]
  clients: Client[]
  directory: DirectoryEntry[]
  onToggle: (e: E) => void
}) {
  if (events.length === 0) {
    return <EmptyState title="No events" description="Nothing scheduled for this date yet." />
  }
  return (
    <div className="flex flex-col gap-2.5">
      <AnimatePresence mode="popLayout">
        {events.map((e) => {
          const assignee = directory.find((m) => m.id === e.assigneeId)
          const client = clients.find((c) => c.id === e.clientId)
          return (
            <motion.div
              layout
              key={e.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="flex items-start gap-2.5 rounded-xl border border-shell-border p-3"
            >
              <button onClick={() => onToggle(e)} className="mt-0.5 text-shell-text/60 hover:text-success-500">
                {e.status === DONE_STATUS ? <CheckCircle2 size={16} className="text-success-500" /> : <Circle size={16} />}
              </button>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-medium', e.status === DONE_STATUS ? 'text-shell-text/50 line-through' : 'text-shell-text-active')}>
                  {e.title}
                </p>
                <p className="text-xs text-shell-text">{client?.name}</p>
                <p className="mt-1 text-xs text-shell-text">{assignee?.name}</p>
              </div>
              <span className="rounded-full bg-shell-border/50 px-2 py-0.5 text-[10px] font-medium text-shell-text capitalize">{e.kind}</span>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
